import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { IRoom, IGuest } from '@share/common-types';
import { v4 } from 'uuid';

const defaultAdultsCount = 2;

/**
 * Activities price per adult by age, so every adult needs an entry here even before the
 * guest picker is opened (TRAV23 activities-front merge). Hotels and condos send
 * `adultsCount` and ignore the field; the activities front seeded it the same way.
 * Without it an untouched room searches as zero adults and the API returns nothing.
 */
export const DEFAULT_ADULT_AGE = 18;

export const getEmptyRoom = (adultsCount = defaultAdultsCount): IRoom => {
  return {
    id: v4(),
    adultsCount,
    adultAges: Array(adultsCount).fill(DEFAULT_ADULT_AGE),
    kids: [],
  };
};

export interface IRoomsState {
  roomsSelected: IRoom[];
  rooms: IRoom[];
  error: string | null;
}

const initialState: IRoomsState = {
  roomsSelected: [getEmptyRoom()],
  rooms: [getEmptyRoom()],
  error: null
};

const newRoomAdultsCount = 1;

const hotelsSlice = createSlice({
  name: 'rooms',
  initialState,
  reducers: {
    addEmptyRoom: (state: IRoomsState) => {
      state.roomsSelected.push(getEmptyRoom(newRoomAdultsCount));
    },
    updateAdultsCount: (
      state: IRoomsState,
      { payload }: PayloadAction<{ roomId: string; count: number }>,
    ) => {
      const room = state.roomsSelected.find(({ id }) => id === payload.roomId);

      if (room) {
        const previousCount = room.adultsCount;
        room.adultsCount = payload.count;

        if (!payload.count) {
          room.kids = [];
          room.adultAges = [];
        } else {
          // Keep one age per adult; added adults start at the default, removed ones drop off.
          const ages = room.adultAges ?? [];
          room.adultAges =
            payload.count > previousCount
              ? [...ages, ...Array(payload.count - previousCount).fill(DEFAULT_ADULT_AGE)]
              : ages.slice(0, payload.count);
        }
      }
    },
    /**
     * Activities price per adult by age, so the room picker edits individual adult ages
     * (TRAV23 activities-front merge). Hotel and condo search never dispatch this.
     */
    updateAdultAge: (
      state: IRoomsState,
      { payload }: PayloadAction<{ roomId: string; adultIndex: number; age: number }>,
    ) => {
      const room = state.roomsSelected.find(({ id }) => id === payload.roomId);

      if (room?.adultAges && room.adultAges[payload.adultIndex] !== undefined) {
        room.adultAges[payload.adultIndex] = payload.age;
      }
    },
    addKid: (state: IRoomsState, { payload }: PayloadAction<{ roomId: string; kid: IGuest }>) => {
      const room = state.roomsSelected.find(({ id }) => id === payload.roomId);

      if (room) {
        room.kids?.push(payload.kid);
      }
    },
    updateKidAge: (
      state: IRoomsState,
      { payload }: PayloadAction<{ roomId: string; kidId: string; age: number }>,
    ) => {
      const room = state.roomsSelected.find(({ id }) => id === payload.roomId);

      if (room) {
        const kid = room.kids?.find(({ id }) => id === payload.kidId);

        if (kid) {
          kid.age = payload.age;
        }
      }
    },
    removeRoom: (state: IRoomsState, { payload }: PayloadAction<string>) => {
      state.roomsSelected = state.roomsSelected.filter(({ id }) => id !== payload);
    },
    removeKid: (
      state: IRoomsState,
      { payload }: PayloadAction<{ roomId: string; kidId: string }>,
    ) => {
      const room = state.roomsSelected.find(({ id }) => id === payload.roomId);

      if (room) {
        room.kids = room.kids?.filter(({ id }) => id !== payload.kidId);
      }
    },
    setRooms: (state: IRoomsState, { payload }: PayloadAction<IRoom[]>) => {
      state.roomsSelected = payload;
    },
    applyRooms: (state: IRoomsState) => {
      state.rooms = state.roomsSelected;
    },
    resetSelectedRooms: (state: IRoomsState) => {
      state.roomsSelected = state.rooms;
    },
    resetRooms: () => {
      return initialState;
    },
  },
});

export const roomsActions = hotelsSlice.actions;

export const roomsReducer = hotelsSlice.reducer;
